import {League} from "../../data/models/user_auth/League";
import {
    Alert,
    Avatar,
    Card,
    CardContent,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Snackbar, Theme,
    useMediaQuery
} from "@mui/material";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import CopyToClipboardButton from "../common/CopyToClipboardButton";
import Button from "@mui/material/Button";
import {useEffect, useState} from "react";
import {copyPicks, copyPicksProMod} from "../../apis/Pick";
import Paper from "@mui/material/Paper";
import { styled } from '@mui/material/styles';

type LeagueRowProps = {
    league: League,
    user_id?: number,
    event_id?: string,
    brackets_completed?: Array<number>,
    brackets_completed_pro_mod?: Array<number>,
    event_active?: boolean,
    brackets_open?: boolean,
    pro_mod_brackets_open?: boolean,
    leagues: Array<League>,
    brackets_viewable?: boolean,
    pro_mod_brackets_viewable?: boolean,
    top_moments_bracket?: boolean,
}

export default function LeagueRow(props: Readonly<LeagueRowProps>) {
    const [open, setOpen] = useState(false);
    const [message, setMessage] = useState("");
    const [severity, setSeverity] = useState<"success" | "info" | "warning" | "error">("success");
    const [isMdOrUp, setIsMdOrUp] = useState(useMediaQuery((theme: Theme) => theme.breakpoints.up('md')));
    let completedBrackets = props.brackets_completed ?? [];
    let brackets_completed_pro_mod = props.brackets_completed_pro_mod ?? [];
    
    useEffect(() => {
        const handleResize = () => {
            setIsMdOrUp(window.innerWidth >= 960);
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [isMdOrUp]);

    async function handleCopyPicks(fromLeagueId: number, toLeagueId: number) {
        let response = await copyPicks(localStorage.getItem("access_token") ?? "", localStorage.getItem("refresh_token") ?? "", fromLeagueId, toLeagueId, props.event_id as string);
        if (response.succeeded) {
            completedBrackets.push(toLeagueId);
            setMessage(response.message);
            setSeverity("success");
            setOpen(true);
        } else {
            setMessage(response.message);
            setSeverity("error");
            setOpen(true);
        }
    }

    async function handleCopyProModPicks(fromLeagueId: number, toLeagueId: number) {
        let response = await copyPicksProMod(localStorage.getItem("access_token") ?? "", localStorage.getItem("refresh_token") ?? "", fromLeagueId, toLeagueId, props.event_id as string);
        if (response.succeeded) {
            setMessage(response.message);
            setSeverity("success");
            setOpen(true);
        } else {
            setMessage(response.message);
            setSeverity("error");
            setOpen(true);
        }
    }

    // Styled components for better UI
    const ActionButton = styled(Button)(({ theme }) => ({
        margin: theme.spacing(1),
        backgroundColor: "#486ce7",
        color: "#ffffff",
        width: 'fit-content',
        minWidth: '140px',
        ":hover": {
            backgroundColor: "#c40000",
            color: "#ffffff",
        },
        [theme.breakpoints.down('md')]: {
            width: '100%',
            margin: theme.spacing(0.5),
        }
    }));

    const ProModButton = styled(Button)(({ theme }) => ({
        margin: theme.spacing(1),
        backgroundColor: "#8B4513", // Brown color for Pro Mod
        color: "#ffffff",
        width: 'fit-content',
        minWidth: '140px',
        ":hover": {
            backgroundColor: "#A0522D",
            color: "#ffffff",
        },
        [theme.breakpoints.down('md')]: {
            width: '100%',
            margin: theme.spacing(0.5),
        }
    }));

    return (
        <Card
            component={Paper}
            elevation={3}
            sx={{
                marginY: 2,
                borderRadius: '10px',
            }}
        >
            <Snackbar anchorOrigin={{vertical: "top", horizontal: "center"}} open={open} onClose={() => setOpen(false)}
                      autoHideDuration={6000}>
                <Alert elevation={6} severity={severity} sx={{width: '100%'}} variant="filled">
                    {message}
                </Alert>
            </Snackbar>
            <CardContent>
                <Grid container sx={{
                    display: 'flex',
                    flexDirection: isMdOrUp ? 'row' : 'column',
                    justifyContent: isMdOrUp ? 'flex-start' : 'center',
                    alignItems: 'center',
                    flexWrap: 'nowrap',
                }}>
                    {props.league.image_url !== "" ?
                        <Avatar src={props.league.image_url} sx={{height: '125px', width: '125px'}}/>
                        :
                        <Avatar sx={{height: '75px', width: '75px'}}/>
                    }
                    <Grid container sx={{
                        direction: 'column',
                        justifyContent: isMdOrUp ? 'flex-start' : 'center',
                        alignContent: 'center',
                        alignItems: 'center',
                        overflow: 'ellipsis',
                        flex: 1,
                    }}>
                        <Typography variant={isMdOrUp ? 'h3' : 'h5'} margin={1} fontWeight={'bold'}
                                    align={isMdOrUp ? "left" : "center"}>
                            {props.league.name}
                        </Typography>
                        
                        {/* Main Action Buttons */}
                        <Grid container sx={{
                            direction: 'row',
                            alignItems: 'center',
                            justifyContent: isMdOrUp ? 'flex-start' : 'center',
                            marginLeft: isMdOrUp ? 0 : 2,
                            flexWrap: 'wrap',
                            gap: 1,
                        }}>
                            <ActionButton
                                variant={"contained"}
                                onClick={() => {
                                    window.location.href = "/league/?" + encodeURIComponent("league_id=" + props.league.id);
                                }}>
                                <Typography variant={'h6'}>
                                    View League
                                </Typography>
                            </ActionButton>
                            
                            {props.league.name !== "Drag Race Bracket Bonanza" &&
                            <CopyToClipboardButton
                                text_to_copy={`You have been invited to join ${props.league.name}! The invite code is ${props.league.invite_code}`}
                                text_to_display={"Copy Invite Code"}
                                style={{
                                    margin: '8px',
                                    width: isMdOrUp ? 'fit-content' : '100%',
                                    backgroundColor: "#486ce7",
                                    color: "#ffffff",
                                }}
                            />
                            }
                            
                            {/* Regular Bracket Buttons */}
                            {props.brackets_viewable && props.event_id !== undefined && props.event_active && (!props.top_moments_bracket || props.league.id === 1) &&
                                <ActionButton
                                    variant={"contained"}
                                    onClick={() => {
                                        const bracketPath = props.top_moments_bracket ? "/bracket/top-moments/" : "/bracket/";
                                        window.location.href = bracketPath + "?" + encodeURIComponent("event_id=" + props.event_id + "&league_id=" + props.league.id + "&user_id=" + props.user_id);
                                    }}>
                                    <Typography variant={'h6'}>
                                        {props?.brackets_open ? (completedBrackets.includes(props.league.id) ? "Edit Bracket" : "Fill Out Bracket") : "View Bracket"}
                                    </Typography>
                                </ActionButton>
                            }
                            
                            {/* Pro Mod Bracket Buttons */}
                            {props.pro_mod_brackets_viewable && props.event_id !== undefined && props.event_active &&
                                <ProModButton
                                    variant={"contained"}
                                    onClick={() => {
                                        window.location.href = "/bracket/pro-mod/?" + encodeURIComponent("event_id=" + props.event_id + "&league_id=" + props.league.id + "&user_id=" + props.user_id);
                                    }}>
                                    <Typography variant={'h6'}>
                                        {props?.pro_mod_brackets_open ? (brackets_completed_pro_mod.includes(props.league.id) ? "Edit Pro Mod Bracket" : "Fill Out Pro Mod Bracket") : "View Pro Mod Bracket"}
                                    </Typography>
                                </ProModButton>
                            }
                        </Grid>
                        
                        {/* Copy Brackets Section */}
                        {props.event_id !== undefined && props.event_active && (!props.top_moments_bracket || props.league.id === 1) &&
                            ((props?.brackets_completed && props.brackets_completed.filter((leagueId: number) => {
                                return leagueId !== props.league.id
                            }).length > 0) || 
                            (props?.brackets_completed_pro_mod && props.brackets_completed_pro_mod.filter((leagueId: number) => {
                                return leagueId !== props.league.id
                            }).length > 0)) &&
                            <Grid container sx={{
                                direction: 'column',
                                alignItems: isMdOrUp ? 'flex-start' : 'center',
                                marginTop: 2,
                                marginLeft: isMdOrUp ? 0 : 3,
                                paddingLeft: 1,
                                gap: 2,
                            }}>
                                <Grid container sx={{
                                    direction: 'row',
                                    alignItems: 'center',
                                    justifyContent: isMdOrUp ? 'flex-start' : 'center',
                                    flexWrap: 'wrap',
                                    gap: 2,
                                }}>
                                    {/* Regular Bracket Copy */}
                                    {props?.brackets_completed && props.brackets_completed.filter((leagueId: number) => {
                                        return leagueId !== props.league.id
                                    }).length > 0 &&
                                        <FormControl sx={{
                                            minWidth: '300px',
                                            width: isMdOrUp ? 'fit-content' : '100%',
                                        }}>
                                            <InputLabel id="copy-picks-label">Copy Regular Bracket</InputLabel>
                                            <Select
                                                labelId="copy-picks-label"
                                                id="copy-picks-select"
                                                name={"copy-picks-select"}
                                                label={"Copy Regular Bracket"}
                                                onChange={(event) => {
                                                    let fromLeagueId = event.target.value as number;
                                                    let toLeagueId = props.league.id;
                                                    handleCopyPicks(fromLeagueId, toLeagueId);
                                                }}
                                            >
                                                {(props.brackets_completed ?? []).filter((leagueId: number) => {
                                                    return leagueId !== props.league.id
                                                }).map((leagueId: number) => {
                                                    return (
                                                        <MenuItem key={leagueId}
                                                                  value={leagueId}>{props.leagues.find((league) => {
                                                            return league.id === leagueId
                                                        })?.name ?? ""}</MenuItem>
                                                    )
                                                })}
                                            </Select>
                                        </FormControl>
                                    }
                                    
                                    {/* Pro Mod Bracket Copy */}
                                    {props?.brackets_completed_pro_mod && props.brackets_completed_pro_mod.filter((leagueId: number) => {
                                        return leagueId !== props.league.id
                                    }).length > 0 &&
                                        <FormControl sx={{
                                            minWidth: '300px',
                                            width: isMdOrUp ? 'fit-content' : '100%',
                                        }}>
                                            <InputLabel id="copy-pro-mod-picks-label">Copy Pro Mod Bracket</InputLabel>
                                            <Select
                                                labelId="copy-pro-mod-picks-label"
                                                id="copy-pro-mod-picks-select"
                                                name={"copy-pro-mod-picks-select"}
                                                label={"Copy Pro Mod Bracket"}
                                                onChange={(event) => {
                                                    let fromLeagueId = event.target.value as number;
                                                    let toLeagueId = props.league.id;
                                                    handleCopyProModPicks(fromLeagueId, toLeagueId);
                                                }}
                                            >
                                                {(props.brackets_completed_pro_mod ?? []).filter((leagueId: number) => {
                                                    return leagueId !== props.league.id
                                                }).map((leagueId: number) => {
                                                    return (
                                                        <MenuItem key={leagueId}
                                                                  value={leagueId}>{props.leagues.find((league) => {
                                                            return league.id === leagueId
                                                        })?.name ?? ""}</MenuItem>
                                                    )
                                                })}
                                            </Select>
                                        </FormControl>
                                    }
                                </Grid>
                            </Grid>
                        }
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    )
}